import React, { useState, useEffect, useContext } from 'react';
import { FormatDate } from '../../Helpers/Utils';
import axios from 'axios';
import { CoinContext } from '../../context/CoinContext';
import { DotLoader } from '../Elements/LogoLoader';
import WalletTOTPVerification from './WalletTOTPVerification';
import { appContext } from '../../App';
import { auth } from '../auth/Auth';
import { useAccount } from '../../context/AccountContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWallet, faEdit, faPlus, faTriangleExclamation, faCopy, faShieldHalved } from '@fortawesome/free-solid-svg-icons';

interface Props { }


interface WalletRow {
    userName: string;
    wallet: string;
    active: boolean;
    changeTime: string;
    walletType: string;
    id: string;
}

function Wallet(props: Props) {
    const [rows, setRows] = useState<WalletRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [walletInput, setWalletInput] = useState<string>('');
    const [modalIsOpen, setIsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<WalletRow | null>(null);
    const coinContext = useContext(CoinContext);
    const [selectedCoin, setSelectedCoin] = useState<string | null>(null)
    const [saveButtonLoading, setSaveButtonLoading] = useState(false)
    const { authUserEmail } = useContext(appContext);
    const [showTOTPVerification, setShowTOTPVerification] = useState(false);
    const [idToken, setIdToken] = useState<string>('');
    const { activeAccount } = useAccount();


    useEffect(() => {
        if (coinContext) {
            const coin = coinContext.selectedCoin;
            readWalletInfo(coin);
            setSelectedCoin(coin)
        }
    }, [coinContext]);

    // Refresh wallet data when active account changes
    useEffect(() => {
        if (activeAccount && coinContext) {
            const coin = coinContext.selectedCoin;
            readWalletInfo(coin);
        }
    }, [activeAccount]);

    function readWalletInfo(coin: string) {
        setIsLoading(true);
        getWalletInfo(coin, walletRows => {
            const activeWallet = walletRows.find(row => row.active)?.wallet || '';
            setRows(walletRows.reverse());
            setIsLoading(false);
        });
    }

    function getWalletInfo(coin: string, callback: (data: WalletRow[]) => void) {
        axios.get(`/pool/wallet?coin=${coin}`)
            .then(response => {
                callback(response.data);
            }).catch(e => {
                console.error("Error fetching wallet info:", e);
                callback([]);
                showSnackbar('Failed to load wallet info', 'error');
            });
    }

    const showSnackbar = (message: string, type: 'success' | 'error') => {
        setSnackbar({ message, type });
        setTimeout(() => setSnackbar(null), 3000);
    };

    const handleEditWalletOpenModal = (row: WalletRow | null) => {
        if (!row) {
            setWalletInput('');
            setSelectedRow(null);
        } else {
            const walletValue = row.wallet || '';
            setWalletInput(walletValue);
            setSelectedRow(row);
        }

        setIsOpen(true);
    };



    const handleSave = async (e: any) => {
        e.preventDefault();

        if (!walletInput) {
            showSnackbar('Wallet address cannot be empty', 'error');
            return;
        }

        setSaveButtonLoading(true);

        try {
            const user = auth.currentUser;
            if (!user) {
                showSnackbar("User not authenticated", 'error');
                setSaveButtonLoading(false);
                return;
            }

            const token = await user.getIdToken();
            setIdToken(token);

            // Check if TOTP is required
            const totpCheckResponse = await axios.get('/pool/api/totp/check', {
                params: { email: authUserEmail },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (totpCheckResponse.data.requiresTOTP) {
                setShowTOTPVerification(true);
                setSaveButtonLoading(false);
            } else {
                executeSave();
            }
        } catch (error: any) {
            console.error("Error during pre-save check:", error);
            showSnackbar(error.response?.data?.message || "An error occurred.", 'error');
            setSaveButtonLoading(false);
        }
    };

    const executeSave = () => {
        if (!selectedRow) {
            saveWallet(walletInput, coinContext?.selectedCoin || '', (stat, response) => {
                setSaveButtonLoading(false);
                if (stat) {
                    showSnackbar("Wallet added successfully!", 'success');
                    readWalletInfo(coinContext?.selectedCoin || '');
                    setIsOpen(false);
                } else {
                    showSnackbar(response?.message || "An error occurred.", 'error');
                }
            });
            return;
        }

        updateWallet(walletInput, coinContext?.selectedCoin || '', (stat, response) => {
            setSaveButtonLoading(false);

            if (stat) {
                showSnackbar("Wallet updated successfully!", 'success');
                readWalletInfo(coinContext?.selectedCoin || '');
                setIsOpen(false);
            } else {
                showSnackbar(response?.message || "An error occurred.", 'error');
            }
        }, selectedRow);
    };

    const handleTOTPVerified = () => {
        setShowTOTPVerification(false);
        setSaveButtonLoading(true);
        executeSave();
    };

    const handleTOTPCancel = () => {
        setShowTOTPVerification(false);
    };




    function updateWallet(walletId: string, currency: string, callback: (stat: boolean, response?: { message: string }) => void, row: WalletRow) {
        if (!walletId) {
            callback(false, { message: 'Wallet address cannot be empty' });
            return;
        }


        axios.post(`/pool/wallet/add?coin=${currency}`, { walletId, currency, walletType: currency, id: row.id })
            .then(response => {
                const responseMessage = response?.data?.message || "An error occurred";
                const st = response?.data?.status || false;
                callback(st, { message: responseMessage });
            })
            .catch(error => {
                console.error("Error saving wallet:", error);
                const responseMessage = error.response?.data?.message || "An error occurred";
                callback(false, { message: responseMessage });
            });
    }

    function saveWallet(walletId: string, currency: string, callback: (stat: boolean, response?: { message: string }) => void) {

        if (!walletId) {
            callback(false, { message: 'Wallet address cannot be empty' });
            return;
        }

        axios.post(`/pool/wallet/add?coin=${currency}`, { walletId, currency, walletType: currency })
            .then(response => {
                const responseMessage = response?.data?.message || "An error occurred";
                const st = response?.data?.status || false;
                callback(st, { message: responseMessage });
            })
            .catch(error => {
                console.error("Error saving wallet:", error);
                const responseMessage = error.response?.data?.message || "An error occurred";
                callback(false, { message: responseMessage });
            });
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 px-4 sm:px-2 py-4 sm:py-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg sm:text-xl font-black text-slate-800 dark:text-white tracking-tight">
                            Payout Addresses
                        </h2>
                    </div>
                    <span className="sm:hidden text-xs font-bold text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full">
                        {rows.length}
                    </span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                            Current Account:
                        </p>
                        <span className="px-3 py-1.5 bg-gradient-to-r from-[#0A3A6B]/10 to-blue-500/10 dark:from-blue-900/20 dark:to-blue-800/20 text-[#0A3A6B] dark:text-blue-400 font-bold rounded-full text-sm">
                            {activeAccount || 'Primary'}
                        </span>
                    </div>

                    <span className="hidden sm:inline-flex items-center text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded-full border border-blue-100 dark:border-blue-800/50">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        {rows.length} Configured
                    </span>
                </div>
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center p-20 bg-white/40 dark:bg-slate-800/40 backdrop-blur-md rounded-[2.5rem] border border-white/20 dark:border-slate-700/50">
                    <DotLoader />
                    <p className="mt-4 text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Fetching Addresses...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 bg-amber-50/50 dark:bg-amber-900/10 backdrop-blur-md rounded-[2.5rem] border border-amber-200 dark:border-amber-800/30 text-center group">
                    <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="text-3xl text-amber-500" />
                    </div>
                    <h3 className="text-base md:text-lg font-black text-amber-800 dark:text-amber-400 uppercase tracking-tight">Wallet Missing</h3>
                    <p className="text-amber-700/60 dark:text-amber-400/60 text-xs md:text-sm mt-2 max-w-xs leading-relaxed">
                        No address found for {selectedCoin}. Add one to start withdrawing.
                    </p>
                    <button
                        onClick={() => handleEditWalletOpenModal(null)}
                        className="mt-6 px-8 py-3 bg-amber-500 text-white rounded-full font-bold text-sm hover:bg-amber-600 transition-all active:scale-95"
                    >
                        Add {selectedCoin} Wallet
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {rows.map((row) => (
                        <div
                            key={row.id}
                            className="group relative bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl p-6 rounded-[2rem] border border-white/40 dark:border-slate-700/50 shadow-lg hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 overflow-hidden"
                        >
                            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform shrink-0">
                                        <FontAwesomeIcon icon={faWallet} className="text-xl" />
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-base md:text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">{row.walletType}</h3>
                                            <span className={`px-2 py-0.5 rounded text-[9px] md:text-[10px] font-bold uppercase tracking-widest ${row.active ? 'bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500'}`}>
                                                {row.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <code className="text-xs font-mono text-slate-500 dark:text-slate-400 truncate max-w-[200px] md:max-w-md">{row.wallet}</code>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(row.wallet);
                                                    showSnackbar('Copied!', 'success');
                                                }}
                                                className="text-slate-300 hover:text-blue-500 transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faCopy} className="text-[10px]" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-right hidden md:block">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Last Updated</p>
                                        <p className="text-[11px] font-bold text-slate-600 dark:text-slate-300"><FormatDate date={row.changeTime} spliter={<></>} /></p>
                                    </div>
                                    {/* <button
                                        onClick={() => handleEditWalletOpenModal(row)}
                                        className="px-6 py-2.5 bg-[#0A3A6B] dark:bg-slate-700 text-white rounded-xl font-bold text-sm hover:opacity-90 transition-all active:scale-95 shadow-md shadow-blue-500/10"
                                    >
                                        Update
                                    </button> */}
                                </div>
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={() => handleEditWalletOpenModal(null)}
                        className="w-full mt-2 py-5 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-[2rem] text-slate-400 dark:text-slate-500 font-bold hover:border-blue-400 hover:text-blue-500 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-all flex items-center justify-center gap-3 group"
                    >
                        <FontAwesomeIcon icon={faPlus} className="group-hover:rotate-90 transition-transform" />
                        <span>Update</span>
                    </button>
                </div>
            )}

            {/* Modal */}
            <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 transition-all duration-300 ${modalIsOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-md"
                    onClick={() => setIsOpen(false)}
                ></div>

                {/* Modal Content */}
                <div className={`relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-3xl sm:rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200/50 dark:border-slate-700/50 transform transition-all duration-300 ${modalIsOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`}>
                    <div className="p-6 sm:p-8">
                        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                                <FontAwesomeIcon icon={faWallet} className="text-lg sm:text-xl" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-base sm:text-lg md:text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
                                    {selectedRow ? `Update ${selectedCoin} Address` : `Add ${selectedCoin} Address`}
                                </h3>
                                <p className="text-slate-500 dark:text-slate-400 text-[10px] sm:text-xs font-bold uppercase tracking-widest mt-1">Configure your payout receiver</p>
                            </div>
                        </div>

                        <form onSubmit={handleSave} className="space-y-5 sm:space-y-6">
                            <div>
                                <label className="block text-slate-700 dark:text-slate-300 text-xs font-bold mb-3 uppercase tracking-widest ml-1">
                                    Wallet Address
                                </label>
                                <input
                                    type="text"
                                    className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-xl sm:rounded-2xl p-3.5 sm:p-4 focus:ring-2 focus:ring-blue-500/50 outline-none transition-all shadow-inner font-mono text-xs sm:text-sm"
                                    placeholder={`Enter your ${selectedCoin} address`}
                                    value={walletInput}
                                    onChange={(e) => setWalletInput(e.target.value)}
                                />
                                <p className="mt-3 text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest px-1 leading-relaxed">
                                    Please verify your address. Incorrect addresses may result in permanent loss of funds.
                                </p>
                            </div>

                            <div className="flex gap-3 sm:gap-4 pt-2 sm:pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="flex-1 py-3.5 sm:py-4 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl sm:rounded-2xl font-bold uppercase tracking-widest text-[10px] sm:text-xs hover:bg-slate-200 dark:hover:bg-slate-600 transition-all active:scale-95"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saveButtonLoading || !walletInput}
                                    className="flex-[2] py-3.5 sm:py-4 bg-gradient-to-r from-[#0A3A6B] to-blue-600 dark:from-blue-600 dark:to-blue-700 text-white rounded-xl sm:rounded-2xl font-bold uppercase tracking-widest text-[10px] sm:text-xs hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-blue-500/30 active:scale-95"
                                >
                                    {saveButtonLoading ? 'Saving...' : 'Confirm Address'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* TOTP Verification */}
            {showTOTPVerification && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 dark:bg-black/80 backdrop-blur-md p-4 sm:p-6">
                    <div className="w-full max-w-md">
                        <WalletTOTPVerification
                            email={authUserEmail}
                            idToken={idToken}
                            onVerify={handleTOTPVerified}
                            onCancel={handleTOTPCancel}
                        />
                    </div>
                </div>
            )}

            {/* Snackbar */}
            {snackbar && (
                <div className={`fixed bottom-8 right-8 z-[100] px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right-10 duration-500 ${snackbar.type === 'success' ? 'bg-green-600 text-white' : 'bg-rose-600 text-white'}`}>
                    <FontAwesomeIcon icon={snackbar.type === 'success' ? faCopy : faTriangleExclamation} />
                    <span className="text-sm font-bold uppercase tracking-widest">{snackbar.message}</span>
                </div>
            )}
        </div>
    );
}

export default Wallet;